import { isNullOrEmpty } from "@/common/util/helpers";
import { Tooltip } from "@ad/anq-ui";
import numeral from "numeral";
import React from "react";
import moment, { Moment } from "moment";
import { DirectionArrowDown, DirectionArrowUp } from "@/Icons";
import classNames from "classnames";

/** 渲染价格(分) */
export const renderCentPrice = (price: number) => {
  return isNullOrEmpty(price)
    ? "-"
    : numeral((price / 100).toFixed(2)).format("0,0.00");
};

/** 渲染价格(元) */
export const renderPrice = (price: number) => {
  return isNullOrEmpty(price)
    ? "-"
    : numeral(price.toFixed(2)).format("0,0.00");
};

/** 千分渲染数字 */
export const renderNumber = (num, precision = 0) => {
  return isNullOrEmpty(num)
    ? "-"
    : numeral(num).format(`0,0${precision ? "." + "0".repeat(precision) : ""}`);
};

/** 不需要管第二个数字什么意思, 反正传一个数字肯定对, 比如0.01 */
export const renderPercent = (percent, divideNumber = 100) => {
  return isNullOrEmpty(percent)
    ? "-"
    : numeral(percent).divide(100).divide(divideNumber).format("0.00%");
};

/**
 * 前面一个上下的箭头, 后面是数字: > 0 为红色, < 0 为绿色
 */
export const renderUpDown = (num: number, unit?: string) => {
  if (!num) {
    return (
      <span
        className={classNames(
          "flex items-center text-xs text-text-black/[.65]"
        )}
      >
        <span className="ml-[2px]">-</span>
      </span>
    );
  }

  return (
    <span
      className={classNames(
        "flex items-center text-xs",
        Number(num) > 0 ? "text-[#FF2424]" : "text-[#00B42B]"
      )}
    >
      {Number(num) > 0 ? (
        <DirectionArrowUp className="text-[14px]" />
      ) : (
        <DirectionArrowDown className="text-[14px] " />
      )}
      <span className="ml-[2px]">
        {Math.abs(Number(num))}
        {unit}
      </span>
    </span>
  );
};

/**
 * @param percent 0 ~ 1 之间的数字
 * @example 0.55 => 55% ~ 60%
 * @example 0.90 => 大于 90%
 * @example 0.08 => 小于 10%
 */
export const renderPercentRange = (percent: number) => {
  if (percent >= 0.9) {
    return "大于 90%";
  }

  if (percent < 0.1) {
    return "小于 10%";
  }

  const times = Math.floor((percent * 100) / 5);

  return `${times * 5}% ~ ${times * 5 + 5}%`;
};

export const renderTitle = (title, notice) => (
  <Tooltip title={notice} trigger="hover">
    <span
      style={{
        borderBottom: "1px dotted rgba(56,60,80,0.35)",
        fontSize: 12,
        paddingBottom: 2,
      }}
    >
      {title}
    </span>
  </Tooltip>
);

export const BIG_NUMBER_MIN_TEXT = "小于 0.1 万";

/**
 * 将大数字, 主要是价格, 展示为 万 或 亿 或其他
 * @example /pro/crowd?menu=crowd_distribute
 *
 * @param value 数字
 * @param fixed 保留几位小数
 * @param addComma 是否添加逗号
 * @param useReal 当不足1000的时候, 是否使用真实的数字表达, 例如 useReal = true, 999 => 999 而不是 999 => "小于 0.1 万"
 */
export const renderBigNumber = (
  value: number | undefined,
  fixed = 2,
  addComma = true,
  useReal = false
) => {
  if (value !== 0 && !value) {
    return "-";
  } else if (value < 1000) {
    return useReal ? value : BIG_NUMBER_MIN_TEXT;
  } else if (value < 100000000) {
    return `${
      addComma
        ? (+(value / 10000).toFixed(fixed)).toLocaleString()
        : (value / 10000).toFixed(fixed)
    } 万`;
  } else {
    return `${
      addComma
        ? (+(value / 100000000).toFixed(fixed)).toLocaleString()
        : (value / 100000000).toFixed(fixed)
    } 亿`;
  }
};

/**
 * 讲一个数字扩展为一个范围, 左闭右开
 * @example 11111 => 10000 ~ 20000
 * @example 10000 => 10000 ~ 20000
 * @example 99 => 90 ~ 100
 *
 * @param min 最小多少
 * @example min=10000, 9999 => 0 ~ 10000
 * @example min=10000, 11111 => 10000 ~ 20000
 */
export const renderNumberRange = (num: number, min = 0) => {
  if (num < min) {
    return [0, min];
  }

  let restNum = num;
  let powerTen = 0;

  while (restNum > 10) {
    restNum /= 10;
    powerTen += 1;
  }

  const left = Math.floor(restNum) * 10 ** powerTen;
  const right =
    (Math.ceil(restNum) === Math.floor(restNum)
      ? Math.ceil(restNum) + 1
      : Math.ceil(restNum)) *
    10 ** powerTen;

  return [left, right];
};

/** 对tips里面的a标签处理一下 */
export const renderTips = (v = "") => {
  return v.replace(/<a /, "<a class=\"dmp-link\" style=\"color: #4C67FF;\"");
};

export const renderFrontendDate = (
  date: string | string[] | Moment | Moment[]
) => {
  if (Array.isArray(date)) {
    return date.map((d) => moment(d).format("YYYY/MM/DD")).join(" ~ ");
  }

  return date ? moment(date).format("YYYY/MM/DD") : "-";
};

export const renderBackendDate = (date: string | string[] | Moment) => {
  if (Array.isArray(date)) {
    return date.map((d) => moment(d).format("YYYY-MM-DD")).join(" ~ ");
  }

  return moment(date).format("YYYY-MM-DD");
};

/**
 * 高亮文本
 * chrome 105 支持 ::highlight() 伪类, 后续考虑使用
 * @param text 文本
 * @param highlight 要高亮的文本
 */
export const renderHighlight = (text: string, highlight: string) => {
  const split = text.split(highlight);

  return (
    <>
      {split.map((item, index) => {
        return (
          <React.Fragment key={index}>
            {item}
            {index !== split.length - 1 && (
              <span className="text-text-blue">{highlight}</span>
            )}
          </React.Fragment>
        );
      })}
    </>
  );
};
